import React, { useEffect, PropsWithChildren, useMemo, useState } from "react";
import { Route, Routes } from "react-router";
import { loadStripe } from "@stripe/stripe-js";
import Restaurant from "./components/restaurant/Restaurant";
import MenuQuid from "./components/menuquid/MenuQuid";
import DeliveryDetails from "./components/info_details/InfoDetails";
import Signin from "./components/signin/Signin";
import OrderRecap from "./components/recap/OrderRecap";
import LocationServiceAvailable from "./components/location_service_available/LocationServiceAvailable";
import ThanksPage from "./components/thanks_page/ThanksPage";
import WaitingOrderApprovement from "./components/waiting_order_approvement/WaitingOrderApprovement";
import { LoaderGif } from './components/loader';
import NotificationOffline from './components/offline';
import { LocalizerHelper } from "./helpers/localizerHelper";
import './app.scss';
import MenuScreen from "./components/menu/MenuScreen";
import QuidScreen from "./components/quid/QuidScreen";
import { auth, initRemoteConfig } from "./firebase";
import Payment from "./components/payment/Payment";
import { RoutesPath } from "./routes/routes";
import { BrowserRouter } from "react-router-dom";
import RouteGuard from "./routes/routeGuard";
import { useCentralizedUserListener } from "./hooks/useCentralizedUserListener";
import { useCentralizedListeners } from "./hooks/useCentralizedListeners";

const ChuzeatWebApp: React.FC<PropsWithChildren<{}>> = () => {
  useCentralizedUserListener(); // Firebase auth + Firestore user
  useCentralizedListeners();

  const user = useState(() => auth.currentUser);

  const stripePromise = useMemo(() => loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY), []);

  useEffect(() => {
    initRemoteConfig();
  }, []);

  const RedirectToHome = () => {
    React.useEffect(() => {
      window.location.href = 'https://app.chuzeat.com/';
    }, []);

    return null;
  };
  
  return (
    <div className="app">
      {!user ? (
        <LoaderGif
        image="/assets/loading_restaurant.gif"
        message={LocalizerHelper.localized('loading_msg_retrieve_services')}
      />
      ) : (
        <BrowserRouter>
            <Routes>
              <Route path={RoutesPath.QUID_HOMEPAGE} element={getRouteGuard(<QuidScreen/>)}/>
              <Route path={RoutesPath.QUID_MENU} element={getRouteGuard(<MenuQuid/>)}/>

              <Route path={RoutesPath.LINK_HOMEPAGE} element={getRouteGuard(<Restaurant/>)}/>
              <Route path={RoutesPath.LINK_LOCATION} element={getRouteGuard(<LocationServiceAvailable/>)}/>
              <Route path={RoutesPath.LINK_MENU} element={getRouteGuard(<MenuScreen/>)}/>
              <Route path={RoutesPath.LINK_RECAP} element={getRouteGuard(<OrderRecap isRecap={true} />)}/>
              <Route path={RoutesPath.LINK_INFO_DETAILS} element={getRouteGuard(<DeliveryDetails/>)}/>
              <Route path={RoutesPath.LINK_CONFIRM_ORDER} element={getRouteGuard(<OrderRecap isRecap={false}/>)}/>
              <Route path={RoutesPath.LINK_WAITING_APPROVAL} element={getRouteGuard(<WaitingOrderApprovement/>)}/>
              <Route path={RoutesPath.LINK_THANKS} element={getRouteGuard(<ThanksPage/>)}/>
              <Route path={RoutesPath.LINK_SIGNIN} element={getRouteGuard(<Signin/>)}/>
              <Route path={RoutesPath.LINK_PAYMENT} element={getRouteGuard(<Payment stripePromise={stripePromise} />)}/>

              <Route path='*' element={<RedirectToHome />}/>
            </Routes>
            <NotificationOffline />
        </BrowserRouter>
      )}
    </div>
  );
};

const getRouteGuard = (component: JSX.Element) => {
  return <RouteGuard>{component}</RouteGuard>;
};

export default ChuzeatWebApp;
